import { useApi } from "@/hooks";
import { BybButton } from "@Components/common/byb-ds";
import BybListSubheader from "@Components/common/byb-ds/lists/byb-list-subheader";
import { ListItemCell, ListItemLayout } from "@Components/game/common";
import SessionResultsLoadingOverlay from "@Components/game/common/session-results-loading-overlay";
import { ScenarioEditDialog, ScenarioFilterDialog, ScenarioListItem } from "@Components/game/scenario-editor";
import ContainerWrapper from "@Components/generic/container-wrapper";
import Icon from "@Components/generic/icon";
import TitleWrapper from "@Containers/shared/title-wrapper";
import { AppContext, Events, UnityBridge } from "@Context/common/general/context";
import { faBug, faPlusCircle, faRefresh, faSearch, faSliders } from "@fortawesome/pro-solid-svg-icons";
import useCompletePlaySession from "@hooks/game/use-complete-play-session";
import { CircularProgress, Container, InputAdornment, List, Stack, TextField, Typography, useTheme } from "@mui/material";
import { Api } from "@Shared/schemas";
import { ScenarioResponse } from "@Shared/schemas/scenarios/scenarioSchemas";
import { useContext, useEffect, useRef, useState } from "react";
import { useDebounce } from "react-use";

//@ts-ignore
import LayoutContentWrapper from "@Components/common/layout-content-wrapper";
import { Filter } from "@Components/game/scenario-editor/scenario-filters-dialog";
import KeyboardKeys from "@Enums/KeyboardKeys";
import PhaseOfPlay from "@Enums/PhaseOfPlay";
import PlayPositions from "@Enums/PlayPositions";
import ftwelve from "f-twelve";
import { isBoolean } from "lodash-es";

const DEFAULT_PAGE = 1;

const getScenarioEditorPageStorageKey = (accountId?: number | null) => `coach-360-scenario-editor-page:${accountId ?? "unknown"}`;

const getStoredScenarioEditorPage = (accountId?: number | null) => {
  if (typeof window === "undefined") return DEFAULT_PAGE;

  const storedPage = Number(window.sessionStorage.getItem(getScenarioEditorPageStorageKey(accountId)));
  return Number.isInteger(storedPage) && storedPage > 0 ? storedPage : DEFAULT_PAGE;
};

const ScenarioEditor = () => {
  const { state, dispatch } = useContext(AppContext);
  const initialPageRef = useRef(getStoredScenarioEditorPage(state.account.id));
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filter, setFilter] = useState<Filter | undefined>();
  const [hasFilters, setHasFilters] = useState(false);
  const [scenarioOpen, setScenarioOpen] = useState(false);
  const [scenarioEditorData, setScenarioEditorData] = useState(null);
  const [saveNewScenarioModal, setSaveNewScenarioModal] = useState(false);
  const [saveScenarioChangesModal, setSaveScenarioChangesModal] = useState(false);
  const [consoleEnabled, setConsoleEnabled] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [throttledSearchQuery, setThrottledSearchQuery] = useState("");
  const [throttledLoading, setThrottledLoading] = useState(false);
  const [page, setPage] = useState(initialPageRef.current);
  const [pageInput, setPageInput] = useState(initialPageRef.current.toString());
  const pageSize = 25;
  const [scenarios, setScenarios] = useState<ScenarioResponse[] | null>(null);
  const [scenarioTotal, setScenarioTotal] = useState(0);
  const theme = useTheme();
  const scrollRef = useRef<HTMLDivElement>(null);
  const hasMountedFiltersRef = useRef(false);
  const totalPages = Math.max(1, Math.ceil((scenarioTotal || 0) / pageSize));

  const [, cancel] = useDebounce(
    () => {
      setThrottledSearchQuery(searchQuery);
    },
    700,
    [searchQuery]
  );

  const {
    call: fetchScenarios,
    data: fetchScenarioResponse,
    loading,
  } = useApi<typeof Api.Scenarios.GetScenarios>({
    api: Api.Scenarios.GetScenarios,
    request: {
      account_id: state.account.id,
      search_query: throttledSearchQuery,
      filter_published: isBoolean(filter?.published) ? filter.published : undefined,
      filter_phase_of_play: filter?.phase_of_play as PhaseOfPlay | undefined,
      filter_tags: filter?.tags as string[],
      filter_play_positions: filter?.play_positions as PlayPositions[] | undefined,
      offset: (page - 1) * pageSize,
      limit: pageSize,
    },
  });

  useEffect(() => {
    if (!fetchScenarioResponse) return;

    const nextTotalPages = Math.max(1, Math.ceil((fetchScenarioResponse.total || 0) / pageSize));

    setScenarioTotal(fetchScenarioResponse.total);

    if (page > nextTotalPages) {
      setPage(nextTotalPages);
      return;
    }

    setScenarios([...fetchScenarioResponse.scenarios]);
    scrollRef.current?.scrollIntoView({ behavior: "instant" });
  }, [fetchScenarioResponse, page, pageSize]);

  useEffect(() => {
    setHasFilters(filter ? Object.values(filter).some((value) => value !== undefined) : false);
  }, [filter]);

  const [, cancelLoading] = useDebounce(
    () => {
      setThrottledLoading(loading);
    },
    500,
    [loading]
  );

  useEffect(() => {
    if (!hasMountedFiltersRef.current) {
      hasMountedFiltersRef.current = true;
      return;
    }

    setPage(DEFAULT_PAGE);
  }, [throttledSearchQuery, filter]);

  useEffect(() => {
    fetchScenarios();
  }, [throttledSearchQuery, page, pageSize, filter]);

  useEffect(() => {
    setPageInput(page.toString());
    if (typeof window === "undefined") return;
    window.sessionStorage.setItem(getScenarioEditorPageStorageKey(state.account.id), page.toString());
  }, [page, state.account.id]);

  useEffect(() => {
    const storedPage = getStoredScenarioEditorPage(state.account.id);
    setPage(storedPage);
    setPageInput(storedPage.toString());
  }, [state.account.id]);

  const requestScenarioDataFromEditor = ({ successCallback }: { successCallback: any }) => {
    UnityBridge.dispatchWebEvent({
      type: UnityBridge.OutgoingWebEvents.EditorScenarioDataRequested,
      successCallback,
    });
  };

  const onEditorSaveAsNewScenario = () => {
    requestScenarioDataFromEditor({
      successCallback: (data: any) => {
        UnityBridge.dispatchWebEvent({
          type: UnityBridge.OutgoingWebEvents.ShowWebUI,
        });
        setScenarioEditorData(data);
        setSaveNewScenarioModal(true);
      },
    });
  };

  const onEditorSaveScenarioChanges = () => {
    requestScenarioDataFromEditor({
      successCallback: (data: any) => {
        UnityBridge.dispatchWebEvent({
          type: UnityBridge.OutgoingWebEvents.ShowWebUI,
        });
        setScenarioEditorData(data);
        setSaveScenarioChangesModal(true);
      },
    });
  };

  const onEditorExited = () => {
    setScenarioEditorData(null);
  };

  const showPlaylistResults = (resultsData: any) => {
    // Todo - Add result event types
    Events.app.navigate.emit({
      path: `/coach/360/scenario-editor/results/${resultsData.play_session_id}`,
      data: resultsData,
    });
  };

  const { isCompletingSession } = useCompletePlaySession({
    onSuccess: showPlaylistResults,
  });

  const onScenarioClose = () => {
    // Only hide WebUI if modal is triggered from editor (save new & save changes)
    if (saveNewScenarioModal || saveScenarioChangesModal) {
      UnityBridge.dispatchWebEvent({
        type: UnityBridge.OutgoingWebEvents.HideWebUI,
      });
    }
    setScenarioOpen(false);
    setSaveScenarioChangesModal(false);
  };

  const toggleConsole = () => {
    if (consoleEnabled) {
      ftwelve.disable();
    } else {
      ftwelve.enable();
    }
    setConsoleEnabled(!consoleEnabled);
  };

  const onUpdateOrCreate = () => {
    setTimeout(() => {
      fetchScenarios();
    }, 500);
  };

  const handleImportClick = () => {
    Events.app.navigate.emit({
      path: `coach/360/matches`,
    });
  };

  useEffect(() => {
    state.editor.events.editorSaveAsNewScenario.on(onEditorSaveAsNewScenario);
    state.editor.events.editorSaveScenarioChanges.on(onEditorSaveScenarioChanges.bind(this));
    state.editor.events.editorExited.on(onEditorExited);
  }, []);

  const handleGoToPage = () => {
    const requestedPage = Number(pageInput);

    if (!Number.isFinite(requestedPage) || requestedPage < 1) {
      setPageInput(page.toString());
      return;
    }

    setPage(Math.min(requestedPage, totalPages));
  };

  const scenariosToRender = scenarios || [];
  const isVR = state.device.isVR && state.device.isUnityEmbedded;

  return (
    <ContainerWrapper id="SCENARIO-EDITOR" enableBackButton={isVR}>
      <Container maxWidth={"md"} sx={{ padding: theme.spacing(6, 0) }}>
        <LayoutContentWrapper>
          <div ref={scrollRef} />
          <TitleWrapper
            title={`Scenarios (${scenarioTotal})`}
            headerContent={
              <Stack direction="row" spacing={2} alignItems={"center"}>
                <TextField
                  disabled={throttledLoading}
                  variant="outlined"
                  size="small"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon icon={faSearch} />
                      </InputAdornment>
                    ),
                  }}
                />
                <BybButton
                  disabled={throttledLoading}
                  sx={{ width: "64px" }}
                  color="secondary"
                  icon={<Icon icon={faBug} />}
                  onClick={toggleConsole}
                />
                <BybButton
                  disabled={throttledLoading}
                  sx={{ width: "64px" }}
                  color={hasFilters ? "primary" : "secondary"}
                  icon={<Icon icon={faSliders} />}
                  onClick={() => setFiltersOpen(true)}
                />
                <BybButton
                  disabled={throttledLoading}
                  sx={{ width: "64px" }}
                  color="secondary"
                  icon={<Icon icon={faRefresh} />}
                  onClick={() => fetchScenarios()}
                />
                <BybButton
                  disabled={throttledLoading}
                  sx={{ width: "128px" }}
                  color="primary"
                  icon={<Icon icon={faPlusCircle} />}
                  onClick={() => handleImportClick()}
                >
                  Import
                </BybButton>
                <BybButton
                  disabled={throttledLoading}
                  sx={{ width: "128px" }}
                  color="primary"
                  icon={<Icon icon={faPlusCircle} />}
                  onClick={() => setScenarioOpen(true)}
                >
                  Create
                </BybButton>
              </Stack>
            }
          >
            {throttledLoading ? (
              <CircularProgress sx={{ alignSelf: "center", marginTop: theme.spacing(40) }} />
            ) : (
              <>
                <List
                  subheader={
                    <BybListSubheader>
                      <ListItemLayout columns={6}>
                        <ListItemCell first subHeader colSpan={2}>
                          <Typography variant="textBase600">Name</Typography>
                        </ListItemCell>
                        <ListItemCell>
                          <Typography variant="textBase600">Tags</Typography>
                        </ListItemCell>
                        <ListItemCell>
                          <Typography variant="textBase600">Phase of play</Typography>
                        </ListItemCell>
                        <ListItemCell>
                          <Typography variant="textBase600">Positions</Typography>
                        </ListItemCell>
                        {/* <ListItemCell>
                      <Typography variant="textBase600">Difficulty</Typography>
                    </ListItemCell> */}
                      </ListItemLayout>
                    </BybListSubheader>
                  }
                >
                  {scenariosToRender.map((scenario: ScenarioResponse) => (
                    <ScenarioListItem
                      key={scenario.id}
                      scenario={scenario}
                      scenarioEditorData={scenarioEditorData}
                      onUpdate={onUpdateOrCreate}
                      onDelete={fetchScenarios}
                    />
                  ))}
                </List>
                {scenariosToRender.length > 0 && (
                  <Stack
                    sx={{ width: "100%", paddingBottom: theme.spacing(3) }}
                    direction="row"
                    alignItems={"center"}
                    spacing={theme.spacing(2)}
                    justifyContent={"right"}
                  >
                    <BybButton color="primary" sx={{ width: "100px" }} onClick={() => setPage((prev) => Math.max(prev - 1, 1))} disabled={page === 1}>
                      Previous
                    </BybButton>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="textBase600" sx={{ color: theme.palette.dsBase.v200 }}>
                        Page
                      </Typography>
                      <TextField
                        size="small"
                        value={pageInput}
                        onChange={(e) => setPageInput(e.target.value.replace(/\D/g, ""))}
                        onBlur={handleGoToPage}
                        onKeyDown={(e) => {
                          if (e.key === KeyboardKeys.ENTER) {
                            handleGoToPage();
                          }
                        }}
                        inputProps={{
                          inputMode: "numeric",
                          pattern: "[0-9]*",
                          min: 1,
                          max: totalPages,
                          style: { textAlign: "center" },
                        }}
                        sx={{ width: "72px" }}
                      />
                      <Typography variant="textBase600" sx={{ color: theme.palette.dsBase.v200 }}>
                        of {totalPages}
                      </Typography>
                    </Stack>
                    <BybButton color="primary" sx={{ width: "100px" }} onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))} disabled={page >= totalPages}>
                      Next
                    </BybButton>
                  </Stack>
                )}
              </>
            )}
          </TitleWrapper>
        </LayoutContentWrapper>
        <ScenarioFilterDialog open={filtersOpen} onApply={(filter: Filter | undefined) => setFilter(filter)} onClose={() => setFiltersOpen(false)} />
        <ScenarioEditDialog open={scenarioOpen} onClose={onScenarioClose} onUpdate={onUpdateOrCreate} onCreate={onUpdateOrCreate} />
      </Container>

      <SessionResultsLoadingOverlay open={isCompletingSession} />
    </ContainerWrapper>
  );
};

export default ScenarioEditor;
